import React, { useEffect, useRef, useState } from 'react';
import AddFormButton from './AddFormButton';
import FormCard from './FormCard';
import FieldOptionsPanel from './FieldOptionsPanel';
import { applyPresetMeta, isStatusesField } from '../../data/formFieldPresets';

const LABELS = ['Order', 'Field Label', 'Field Type', 'Required', 'Field Access', 'Hide for', 'DependsOn', 'Options'];

function normalizeField(field, index) {
  const nextField = applyPresetMeta({
    ...field,
    id: field.id ?? `${Date.now()}-${index}`,
    order: index + 1,
    fieldLabel: field.fieldLabel ?? field.name ?? '',
    fieldType: field.fieldType ?? field.typeLabel ?? 'Custom',
    required: Boolean(field.required),
    fieldAccess: field.fieldAccess ?? '',
    hideFor: field.hideFor ?? [],
    dependsOn: field.dependsOn ?? '',
    options: field.options ?? [],
    parentMappings: field.parentMappings ?? {},
    optionColors: field.optionColors ?? {},
    lockMode: field.lockMode,
    isNew: Boolean(field.isNew),
  });
  return nextField;
}

export default function FormCardGrid({
  initialFields,
  onChange,
  addExternalFieldRef,
  fieldTypeOptions = [],
  fieldAccessOptions = [],
  hideForOptions = [],
  savingFieldId = null,
  // deletingFieldId = null,
  onSave,
  onDelete,
}) {
  const [fields, setFields] = useState(() => (initialFields ?? []).map(normalizeField));
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const syncedSignatureRef = useRef('');
  const commit = (nextFields) => { setFields(nextFields); onChange?.(nextFields); };

  const fieldSignature = (initialFields ?? []).map((field) => field.id).join(',');

  useEffect(() => {
    if (!fieldSignature || fieldSignature === syncedSignatureRef.current) return;
    syncedSignatureRef.current = fieldSignature;
    setFields(initialFields.map(normalizeField));
  }, [fieldSignature, initialFields]);

  useEffect(() => {
    if (!addExternalFieldRef) return undefined;
    addExternalFieldRef.current = (fieldToAdd) => {
      if (!fieldToAdd) return;
      setFields((currentFields) => {
        const alreadyAdded = currentFields.some((field) => (
          (fieldToAdd.fieldKey && field.fieldKey === fieldToAdd.fieldKey)
          || field.fieldLabel === fieldToAdd.fieldLabel
        ));
        if (alreadyAdded) return currentFields;
        const nextFields = [...currentFields, normalizeField({ ...fieldToAdd, id: `${fieldToAdd.id}-${Date.now()}` }, currentFields.length)];
        onChange?.(nextFields);
        return nextFields;
      });
    };
    return () => {
      addExternalFieldRef.current = null;
    };
  }, [addExternalFieldRef, onChange]);

  const reorderFields = (fromId, toId) => {
    if (fromId == null || toId == null || String(fromId) === String(toId)) return;
    const fromIndex = fields.findIndex((field) => String(field.id) === String(fromId));
    const toIndex = fields.findIndex((field) => String(field.id) === String(toId));
    if (fromIndex < 0 || toIndex < 0) return;
    const nextFields = [...fields];
    const [moved] = nextFields.splice(fromIndex, 1);
    nextFields.splice(toIndex, 0, moved);
    commit(nextFields.map((item, index) => ({ ...item, order: index + 1 })));
  };
  const updateField = (updatedField) => commit(fields.map((field) => field.id === updatedField.id ? updatedField : field));
  const deleteField = async (id) => {
    const field = fields.find((item) => item.id === id);
    if(!field) return;
    try{
      const removed = await onDelete?.(field);
      if(removed === false) return;
      commit(fields.filter((item) => item.id !== id).map((item, index) => ({...item, order: index + 1})));
    } catch {
      // Keep the field in the grid; delete API error is stored on formFields.error.
    }
  }
  const addField = () => commit([...fields, normalizeField({ id: `${Date.now()}-${fields.length}`, fieldLabel: '', fieldType: fieldTypeOptions[0] ?? '', options: [], isNew: true }, fields.length)]);
  const saveField = async (field) => {
    try {
      const saved = await onSave?.(field);
      if (!saved) return null;
      setFields((currentFields) => {
        const nextFields = currentFields.map((item) => (
          item.id === field.id
            ? normalizeField({ ...saved, isNew: false }, (item.order || 1) - 1)
            : item
        ));
        onChange?.(nextFields);
        return nextFields;
      });
      return saved;
    } catch {
      return null;
    }
  };
  const labels = fields.map((field) => field.fieldLabel).filter(Boolean);
  const selectedField = fields.find((field) => field.id === selectedFieldId)
    || fields.find(isStatusesField)
    || null;

  return (
    <div className="ca-form-card-grid__container ">
    <div className="ca-form-card-grid">
      <div className="ca-form-card-grid__labels">
        {LABELS.map((label) => <div key={label} className="ca-form-card-grid__label">{label}</div>)}
      </div>
      <div className="ca-form-card-grid__cards">
        {fields.map((field) => (
          <FormCard
            key={field.id}
            field={field}
            fieldOptions={labels.filter((label) => label !== field.fieldLabel)}
            fieldTypeOptions={fieldTypeOptions}
            fieldAccessOptions={fieldAccessOptions}
            hideForOptions={hideForOptions}
            isSaving={savingFieldId === field.id}
            selected={selectedField?.id === field.id}
            isDragging={String(draggingId) === String(field.id)}
            onSelect={() => setSelectedFieldId(field.id)}
            onDragStartField={() => setDraggingId(field.id)}
            onDragEndField={() => setDraggingId(null)}
            onReorder={reorderFields}
            // isDeleting={deletingFieldId === field.id}
            onChange={updateField}
            onDelete={deleteField}
            onSave={saveField}
          />
        ))}
        <AddFormButton onAdd={addField} />
      </div>
        
    </div>
        <FieldOptionsPanel formFields={fields} selectedField={selectedField} onChange={commit} />

        </div>
  );
}